import { useEffect, useRef, useState } from "react";

import { storage } from "@/lib/storage";
import { tiptap } from "@/lib/tiptap";

const DRAFT_WRITE_DEBOUNCE_MS = 500;

// Semi auto-save for long-form inputs (notes, evaluations): hydrates the
// initial value from localStorage, debounces writes while typing so we're not
// hitting storage on every keystroke, and drops the entry once the text is
// saved (clearDraft) or emptied. Callers should remount per entity (the sheet
// already keys tabs by candidacy) — the key is only read on mount.
export const useInputDraft = (draftKey: string) => {
  /* React Hooks */
  const [draft, setDraft] = useState(() => storage.getInputDraft(draftKey));
  const draftRef = useRef(draft);

  useEffect(() => {
    draftRef.current = draft;
    const timeout = window.setTimeout(() => {
      // Empty rich text ("<p></p>") counts as removed text, not a draft.
      storage.setInputDraft(draftKey, tiptap.isEmptyFn(draft) ? "" : draft);
    }, DRAFT_WRITE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [draft, draftKey]);

  // Flush the newest keystrokes if the input unmounts inside the debounce
  // window (closing the sheet, switching tabs).
  useEffect(() => {
    return () => {
      const latestDraft = draftRef.current;
      storage.setInputDraft(
        draftKey,
        tiptap.isEmptyFn(latestDraft) ? "" : latestDraft,
      );
    };
  }, [draftKey]);

  /* Handlers */
  const clearDraft = () => {
    draftRef.current = "";
    setDraft("");
    // Written immediately — a save shouldn't leave a stale draft behind if
    // the debounce never fires.
    storage.setInputDraft(draftKey, "");
  };

  return { clearDraft, draft, setDraft };
};
